import { pollDqxRunUntilTerminal } from "@/lib/pipelineDqxTrigger";
import { fetchPipelineDqxRowSummary } from "@/lib/pipelineDqxSummary";
import type { PipelineFileDqxState } from "@/lib/pipelineDqx";

/**
 * Load row summary after a DQ job reaches a terminal Databricks state.
 * A failed job that still wrote quarantine/summary is treated as complete
 * (row issues, not infra miss). Summary miss after a failed job stays failed.
 */
export async function finalizePipelineDqxAfterJobTerminal(
  baseState: PipelineFileDqxState,
  rowCount: number | null | undefined,
  options?: { jobFailed?: boolean }
): Promise<PipelineFileDqxState> {
  const jobFailed = options?.jobFailed ?? false;
  try {
    const summary = await fetchPipelineDqxRowSummary(baseState.jobId, rowCount);
    return {
      ...baseState,
      phase: "complete",
      error: null,
      rowsPassing: summary.rowsPassing,
      rowsWithIssues: summary.rowsWithIssues,
      totalRowsChecked: summary.totalRowsChecked,
    };
  } catch (err) {
    if (jobFailed) {
      return {
        ...baseState,
        phase: "failed",
        error: "Data quality checks failed.",
      };
    }
    return {
      ...baseState,
      phase: "complete",
      message: err instanceof Error ? err.message : "Could not load DQ summary",
      rowsPassing: null,
      rowsWithIssues: null,
      totalRowsChecked: null,
    };
  }
}

export async function finalizePipelineDqxAfterJobSuccess(
  baseState: PipelineFileDqxState,
  rowCount: number | null | undefined
): Promise<PipelineFileDqxState> {
  return finalizePipelineDqxAfterJobTerminal(baseState, rowCount);
}

export async function pollAndFinalizePipelineDqx(
  runId: number,
  baseState: PipelineFileDqxState,
  rowCount: number | null | undefined,
  options?: { signal?: AbortSignal }
): Promise<PipelineFileDqxState> {
  const outcome = await pollDqxRunUntilTerminal(runId, { signal: options?.signal });
  if (outcome !== "success") {
    return finalizePipelineDqxAfterJobTerminal(baseState, rowCount, { jobFailed: true });
  }
  return finalizePipelineDqxAfterJobSuccess(baseState, rowCount);
}
